import { nanoid } from 'nanoid';

import { type SelectRowsEvent } from '../events/types.ts';
import { type SelectionRange, type SelectionState } from '../state/types.ts';

import { type ReducerResponse } from './types.ts';

/**
 * Replaces selection state with finite row ranges and positions focus on the
 * first range or supplied focus override. Empty input is a noop; stored ranges
 * keep segment geometry instead of dense row lists.
 */
export function selectRowsReducer(
  state: SelectionState,
  event: SelectRowsEvent
): ReducerResponse {
  // No-op if no ranges provided
  if (event.ranges.length === 0) {
    return {
      delta: { kind: 'noop' },
      next: state
    };
  }

  // Map event ranges to internal SelectionRange objects
  const newSelections: SelectionRange[] = event.ranges.map((range) => ({
    id: nanoid(),
    anchor: {
      row: range.anchor.row,
      col: { index: 0 }
    },
    lead: {
      row: range.lead.row,
      col: { index: 0 }
    }
  }));

  const primaryRange = event.ranges[0];
  const focusedCell = {
    row: event.focusRow ?? primaryRange.lead.row,
    col: event.focusCol ?? { index: 0 }
  };

  return {
    delta: {
      kind: 'selectionReplaced',
      source: event.source,
      selections: newSelections,
      focusChange: {
        prev: state.focusedCell,
        next: focusedCell
      }
    },

    next: {
      ...state,
      mode: 'row',
      focusedCell,
      selections: newSelections,
      lastInteractionSource: event.source
    }
  };
}
